import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { LegacyProductCard } from '@/components/piece/LegacyProductCard'
import { pieceCardHref, pieceOriginOf } from '@/components/piece/PieceReference'
import { isStructuralPieceAbsence, LogsDisclosure, PieceCharacteristics, TechnicalIdentityLog } from '@/components/warehouse/PieceCharacteristics'
import { ProductCardIdentity } from '@/components/warehouse/ProductCardIdentity'
import { ProductMediaGallery } from '@/components/warehouse/ProductMediaGallery'
import { ProductMovementDetails } from '@/components/warehouse/ProductMovementDetails'
import { ProductOrderHistory } from '@/components/warehouse/ProductOrderHistory'
import { ProductPrices } from '@/components/warehouse/ProductPrices'
import { ProductProblems } from '@/components/warehouse/ProductProblems'
import { ProductAttributionSection } from '@/components/warehouse/ProductAttributionSection'
import { PublishedDescription } from '@/components/warehouse/PublishedDescription'
import { VariationFeed } from '@/components/warehouse/VariationFeed'
import { BudgetedList, Fact, SectionCard, SectionIndex } from '@/components/warehouse/primitives'
import { getInventoryVariant, getProductMedia, getVariantModelGroup, getVariantReconciliation } from '@/lib/api/client/inventory'
import { getProductAttributionEvidence } from '@/lib/api/client/product-attribution'
import type { ProductEvidence } from '@/lib/api/schemas/product-attribution.schema'
import type { InventoryVariant, ModelGroup, ProductMedia, VariantReconciliation } from '@/lib/api/schemas/inventory.schema'
import { PRODUCT_NOT_FOUND, isOutsidePiecePopulation, readLegacyProduct, type LegacyProduct } from '@/lib/recovery/legacy-product'
import {
  accountCodeLabel,
  frozenMappingRefusalLabels,
  holdStateLabels,
  labelFor,
  movementLabels,
  proposalDecisionLabels,
  proposalStateLabels,
  unavailableReasonText,
  warehouseDateTime,
} from '@/lib/warehouse/labels'
import { displayArticle, marketplaceBadges } from '@/lib/warehouse/projection'

export default function ProductPage() {
  const { canonicalVariantId = '' } = useParams()
  const navigate = useNavigate()
  const origin = pieceOriginOf(useLocation().state)
  const [variant, setVariant] = useState<InventoryVariant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [legacy, setLegacy] = useState<LegacyProduct | null>(null)
  const [reconciliation, setReconciliation] = useState<VariantReconciliation | null>(null)
  const [reconciliationLoading, setReconciliationLoading] = useState(true)
  const [reconciliationError, setReconciliationError] = useState<string | null>(null)
  const [media, setMedia] = useState<ProductMedia | null>(null)
  const [mediaLoading, setMediaLoading] = useState(true)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [modelGroup, setModelGroup] = useState<ModelGroup | null>(null)
  const [modelGroupLoading, setModelGroupLoading] = useState(true)
  const [modelGroupError, setModelGroupError] = useState<string | null>(null)
  const [attribution, setAttribution] = useState<ProductEvidence | null>(null)
  const [attributionLoading, setAttributionLoading] = useState(false)
  const [attributionError, setAttributionError] = useState<string | null>(null)
  const [attributionReload, setAttributionReload] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    setLegacy(null)
    getInventoryVariant(canonicalVariantId, controller.signal)
      .then((next) => { if (!controller.signal.aborted) setVariant(next) })
      .catch(async (reason: unknown) => {
        if (controller.signal.aborted || !isOutsidePiecePopulation(reason)) throw reason
        const read = await readLegacyProduct(canonicalVariantId, controller.signal)
        if (controller.signal.aborted) return
        if (read === null) setError(PRODUCT_NOT_FOUND)
        else setLegacy(read)
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        setError(reason instanceof Error ? reason.message : 'Не удалось загрузить изделие.')
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [canonicalVariantId])

  useEffect(() => {
    const controller = new AbortController()
    setModelGroup(null)
    setModelGroupLoading(true)
    setModelGroupError(null)
    getVariantModelGroup(canonicalVariantId, controller.signal)
      .then((next) => { if (!controller.signal.aborted) setModelGroup(next) })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        setModelGroupError(reason instanceof Error ? reason.message : 'Артикулы модели недоступны.')
      })
      .finally(() => { if (!controller.signal.aborted) setModelGroupLoading(false) })
    return () => controller.abort()
  }, [canonicalVariantId])

  // Read-only and detail-only: the list route never asks for this.
  useEffect(() => {
    const controller = new AbortController()
    setReconciliationLoading(true)
    setReconciliationError(null)
    getVariantReconciliation(canonicalVariantId, controller.signal)
      .then((next) => { if (!controller.signal.aborted) setReconciliation(next) })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        setReconciliation(null)
        setReconciliationError(reason instanceof Error ? reason.message : 'Сравнение полей недоступно.')
      })
      .finally(() => { if (!controller.signal.aborted) setReconciliationLoading(false) })
    return () => controller.abort()
  }, [canonicalVariantId])

  // Detail-only, like the reconciliation read above: the list route never asks
  // for media. Addressed by product, which is the grain `product_media` is keyed
  // by, so the panel never reports a variant refusal for a product it is showing.
  const productId = variant?.productId ?? null
  useEffect(() => {
    if (productId === null) return
    const controller = new AbortController()
    setMediaLoading(true)
    setMediaError(null)
    getProductMedia(productId, controller.signal)
      .then((next) => { if (!controller.signal.aborted) setMedia(next) })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        setMedia(null)
        setMediaError(reason instanceof Error ? reason.message : 'Фото и видео недоступны.')
      })
      .finally(() => { if (!controller.signal.aborted) setMediaLoading(false) })
    return () => controller.abort()
  }, [productId])

  useEffect(() => {
    if (productId === null) return
    const controller = new AbortController()
    setAttributionLoading(true)
    setAttributionError(null)
    getProductAttributionEvidence(productId, controller.signal)
      .then((next) => { if (!controller.signal.aborted) setAttribution(next) })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        setAttribution(null)
        setAttributionError(reason instanceof Error ? reason.message : 'Список изделий того же артикула недоступен.')
      })
      .finally(() => { if (!controller.signal.aborted) setAttributionLoading(false) })
    return () => controller.abort()
  }, [productId, attributionReload])

  if (loading) {
    return <div className="grid h-full place-items-center text-sm text-ink-muted" aria-live="polite">Загружаем изделие…</div>
  }
  if (legacy) {
    return <LegacyProductCard product={legacy} origin={origin} onBack={() => navigate(-1)} />
  }
  if (error || !variant) {
    return (
      <div className="grid h-full place-items-center px-4 text-center">
        <div>
          <p role="alert" className="text-sm text-st-unpaid">{error ?? PRODUCT_NOT_FOUND}</p>
          <Link to="/inventory" className="mt-3 inline-block rounded-md border border-rule px-3 py-2 text-xs text-ink-2 hover:bg-bone/60">Вернуться к списку</Link>
        </div>
      </div>
    )
  }

  const badges = marketplaceBadges(variant, null)
  const modelIsStructural = isStructuralPieceAbsence('model', variant.modelArticle)
  const proposals = reconciliation?.reconciliationProposals ?? []
  const hasMovement = variant.movementTotal > 0 || variant.movements.length > 0
  const hasHold = variant.holdTotal > 0 || variant.holds.length > 0
  const showAttribution = attribution !== null && (
    attribution.hasConflicts
    || attribution.siblings.length > 1
    || variant.reviewState === 'conflict'
  )
  const sections = [
    { id: 'media', label: 'Медиа', count: media?.merchandisingTotal ?? 0 },
    { id: 'prices', label: 'Цены' },
    { id: 'characteristics', label: 'Характеристики' },
    { id: 'description', label: 'Описание' },
    { id: 'movement-details', label: 'Состояние и движение' },
    ...(showAttribution ? [{ id: 'same-article', label: 'С таким же артикулом', count: attribution?.hasConflicts ? 1 : undefined, tone: 'warn' as const }] : []),
    ...(hasMovement ? [{ id: 'movements', label: 'Движения остатка', count: variant.movementTotal }] : []),
    ...(hasHold ? [{ id: 'holds', label: 'Защитные удержания', count: variant.holdTotal }] : []),
    ...(variant.frozenRegistry ? [{ id: 'onec-evidence', label: 'Доказательства 1С' }] : []),
    { id: 'logs', label: 'Логи' },
  ]

  return (
    <div className="h-full overflow-y-auto bg-ivory">
      <div className="mx-auto max-w-[1200px] px-3 py-4 pb-[calc(32px+env(safe-area-inset-bottom))] sm:px-5 md:px-7 md:py-6">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          {origin
            ? <Link to={origin.href} data-testid="piece-card-origin" className="rounded-md border border-rule bg-paper px-2.5 py-1.5 text-ink-2 hover:bg-bone/60 hover:text-ink">{origin.label ? `Назад к ${origin.label}` : 'Назад'}</Link>
            : <button type="button" onClick={() => navigate(-1)} className="rounded-md border border-rule bg-paper px-2.5 py-1.5 text-ink-2 hover:bg-bone/60 hover:text-ink">Назад</button>}
          <Link to="/inventory" className="rounded-md px-2 py-1.5 hover:text-ink">Склад</Link>
          <span aria-hidden="true">/</span>
          <span className="truncate text-ink-2">{displayArticle(variant) || variant.title}</span>
        </nav>

        <header className="mt-3 flex flex-wrap items-start justify-between gap-3 border-b border-rule pb-4" title={modelIsStructural ? unavailableReasonText(variant.modelUnavailableReason ?? 'model.grouping_not_reviewed') : undefined}>
          <ProductCardIdentity variant={variant} badges={badges} heading="h1" />
        </header>

        <div className="mt-4">
          {modelGroupLoading ? <p aria-live="polite" className="text-xs text-ink-muted">Загружаем артикулы модели…</p>
            : modelGroupError ? <p role="alert" className="text-xs text-st-unpaid">{modelGroupError}</p>
              : modelGroup ? <VariationFeed model={modelGroup} onPick={(variantId) => navigate(pieceCardHref(variantId), { replace: true, state: origin ? { pieceOrigin: origin } : null })} /> : null}
        </div>

        <div className="mt-4 grid gap-[18px] lg:grid-cols-[198px_minmax(0,1fr)]">
          <aside className="hidden lg:block"><SectionIndex sections={sections} /></aside>
          <main className="min-w-0 space-y-3">
        <div className="space-y-3">
          <div id="media"><SectionCard title={`Медиа · ${media?.merchandisingTotal ?? 0}`} ariaLabel="Фото и видео" subtitle="Порядок фото сохраняется; WB использует отдельную проекцию видео · общие для всех размеров артикула">
            {mediaLoading ? <p aria-live="polite" className="text-xs text-ink-muted">Загружаем фото и видео…</p>
              : mediaError ? <p role="alert" className="text-xs text-st-unpaid">{mediaError}</p>
                : media ? <ProductMediaGallery media={media} columns={6} heading={`${displayArticle(variant)} · ${variant.title}`} onRefresh={setMedia} /> : null}
          </SectionCard></div>

          <ProductProblems
            problems={variant.problems ?? []}
            available={variant.problemsAvailable ?? false}
            unavailableReason={variant.problemsUnavailableReason ?? null}
          />

          <div id="prices"><ProductPrices variant={variant} /></div>

          <div id="characteristics">
            <PieceCharacteristics variant={variant} />
          </div>

          <div id="description">
            <PublishedDescription variant={variant} />
          </div>

          <div id="movement-details">
            <ProductMovementDetails variant={variant} includeReceipt />
            <ProductOrderHistory productId={variant.productId} />
          </div>

          {attributionLoading ? <div id="same-article"><SectionCard title="С таким же артикулом"><p aria-live="polite" className="text-xs text-ink-muted">Загружаем список изделий того же артикула…</p></SectionCard></div> : attributionError && variant.reviewState === 'conflict' ? <div id="same-article"><SectionCard title="С таким же артикулом"><p role="alert" className="text-xs text-st-unpaid">{attributionError}</p></SectionCard></div> : showAttribution && attribution ? <div id="same-article"><ProductAttributionSection evidence={attribution} onReload={() => setAttributionReload((value) => value + 1)} /></div> : null}

          {/*
           * The sales-window box is retired by comparison. On 2026-09-13 over
           * 5,912 live pieces it counted 47 marketplace_order movements and
           * 0 onec_offline_sale movements; 42 of the 47 pieces are represented
           * by the order-history task, while the remaining 5 stay in this
           * all-kind movement list. The old box exposed no buyer, status or
           * amount and showed 0 on sold pieces, so removing it loses no fact.
           */}
          {hasMovement ? <div id="movements"><SectionCard title="Движения остатка">
            {variant.movements.length === 0 ? <p className="text-xs text-ink-muted" data-population-total={variant.movementTotal}>Строки движений не вошли в ограниченное окно.</p> : (
              <BudgetedList budget={3} total={variant.movementTotal} noun="движений" className="space-y-1.5 text-xs">
                {variant.movements.map((movement, index) => (
                  <li key={`${movement.occurredAt}-${index}`} className="flex justify-between gap-3">
                    <span className="text-ink-2">{accountCodeLabel(movement.sourceAccountCode)} · {movementLabels[movement.kind]} · {movement.quantity}</span>
                    <time className="shrink-0 text-ink-muted">{warehouseDateTime(movement.occurredAt)}</time>
                  </li>
                ))}
              </BudgetedList>
            )}
          </SectionCard></div> : null}

          {hasHold ? <div id="holds"><SectionCard title={`Защитные удержания · ${variant.holdTotal}`}>
            {variant.holds.length === 0 ? <p className="text-xs text-ink-muted" data-population-total={variant.holdTotal}>Строки удержаний не вошли в ограниченное окно.</p> : (
              <BudgetedList budget={4} total={variant.holdTotal} noun="удержаний" className="space-y-1.5 text-xs text-ink-2">
                {variant.holds.map((hold, index) => (
                  <li key={`${hold.targetAccountCode}-${index}`}>{accountCodeLabel(hold.sourceAccountCode)} → {accountCodeLabel(hold.targetAccountCode)}: <strong>{hold.quantity}</strong> · {holdStateLabels[hold.state]}</li>
                ))}
              </BudgetedList>
            )}
          </SectionCard></div> : null}

          {variant.frozenRegistry ? (
            <div id="onec-evidence"><SectionCard title="Доказательства 1С" subtitle={`Адаптер ${variant.frozenRegistry.sourceAdapterId} · доказательство ${variant.frozenRegistry.inputDigest.slice(0, 12)}`}>
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Fact label="Физических единиц"><span className="font-medium">{variant.frozenRegistry.physicalUnitCount}</span></Fact>
                <Fact label="Принято привязок"><span className="font-medium">{variant.frozenRegistry.acceptedMappingCount}</span></Fact>
                <Fact label="Отказано привязок"><span className="font-medium">{variant.frozenRegistry.refusedMappingCount}</span></Fact>
                <Fact label="Причины отказа">
                  <span className="text-ink-2">{variant.frozenRegistry.refusedMappingReasons.map((reason) => labelFor(frozenMappingRefusalLabels, reason)).join(', ') || '—'}</span>
                </Fact>
              </dl>
            </SectionCard></div>
          ) : null}

          <LogsDisclosure id="logs">
            <TechnicalIdentityLog variant={variant} />
            <SectionCard title={`Предложения по сведению полей · ${reconciliation?.reconciliationProposalTotal ?? 0}`} ariaLabel="Предложения по сведению полей" subtitle="Только просмотр: в этой фазе ни одно предложение не исполняется.">
              {reconciliationLoading ? <p aria-live="polite" className="text-xs text-ink-muted">Загружаем предложения…</p>
                : reconciliationError ? <p role="alert" className="text-xs text-st-unpaid">{reconciliationError}</p>
                  : proposals.length === 0 ? <p className="text-xs text-ink-muted">Предложения ещё не сформированы.</p> : (
                <BudgetedList budget={3} total={reconciliation?.reconciliationProposalTotal ?? 0} noun="предложений" className="space-y-2 text-xs">
                  {proposals.map((proposal) => (
                    <li key={proposal.proposalId} className="rounded-md border border-rule bg-ivory/50 px-3 py-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-medium text-ink">{proposalDecisionLabels[proposal.decision]}</span>
                        <span className="text-[10px] text-ink-muted">{proposalStateLabels[proposal.state]} · версия {proposal.proposalVersion}</span>
                      </div>
                      <p className="mt-1 text-ink-2">
                        Поле {proposal.field}{proposal.targetAccountCode ? ` · площадка ${proposal.targetAccountCode}` : ''} · {warehouseDateTime(proposal.createdAt)}
                      </p>
                      <p className="mt-1 font-mono text-[10px] text-ink-muted">
                        источник {proposal.sourceHash.slice(0, 10)} · канон {proposal.canonicalHash.slice(0, 10)}{proposal.targetHash ? ` · цель ${proposal.targetHash.slice(0, 10)}` : ''}
                      </p>
                    </li>
                  ))}
                </BudgetedList>
              )}
            </SectionCard>
          </LogsDisclosure>

          <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-rule pt-2 text-[11px]">
            <Link to="/analytics" className="text-ink-muted underline hover:text-ink">Аналитика по товарам</Link>
          </div>
        </div>
          </main>
        </div>
      </div>
    </div>
  )
}
